// admin-web/src/pages/MessagingBroadcastPage.tsx
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { functions } from "../firebase";

type StudentRow = {
  id: string;
  studentKey: string;
  fullName: string;
  grade?: string;
};

type SavedAudience = {
  id: string;
  name: string;
  baseTitle: string;
  studentKeys: string[];
  createdAtIso: string;
};

const STORAGE_KEY = "zca_admin_saved_audiences_v1";

function asString(v: any) {
  return typeof v === "string" ? v.trim() : "";
}

function normalize(v: string) {
  return String(v || "").trim().toLowerCase();
}

function makeId() {
  return Math.random().toString(16).slice(2) + "-" + Date.now().toString(16);
}

function readSavedAudiences(): SavedAudience[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeSavedAudiences(items: SavedAudience[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function buildSmartTitle(baseTitle: string, selectedNames: string[]) {
  const base = baseTitle.trim() || "Announcements";
  if (selectedNames.length === 1) return `${base} – ${selectedNames[0]}`;
  if (selectedNames.length > 1) return `${base} – ${selectedNames.length} students`;
  return base;
}

export function MessagingBroadcastPage() {
  const nav = useNavigate();

  const [students, setStudents] = useState<StudentRow[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(true);

  const [studentFilter, setStudentFilter] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const [baseTitle, setBaseTitle] = useState("Announcements");
  const [autoAppendAudience, setAutoAppendAudience] = useState(true);

  const [savedAudiences, setSavedAudiences] = useState<SavedAudience[]>([]);
  const [saveName, setSaveName] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    setSavedAudiences(readSavedAudiences());
  }, []);

  useEffect(() => {
    (async () => {
      setLoadingStudents(true);
      setErr(null);
      try {
        const fn = httpsCallable(functions, "getMyStudentsForTeacher");
        const res: any = await fn({});
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.students) ? res.data.students : [];

        const out: StudentRow[] = rows.map((x: any) => {
          const studentKey = asString(x.studentKey) || asString(x.id);
          const first = asString(x.firstName);
          const last = asString(x.lastName);
          const fullName =
            asString(x.fullName) ||
            asString(x.displayName) ||
            asString(x.name) ||
            `${first} ${last}`.trim() ||
            studentKey;

          return {
            id: asString(x.id) || studentKey,
            studentKey,
            fullName,
            grade: asString(x.grade),
          };
        });

        out.sort((a, b) => a.fullName.localeCompare(b.fullName));
        setStudents(out);
      } catch (e: any) {
        setErr(e?.message || "Failed to load students.");
      } finally {
        setLoadingStudents(false);
      }
    })();
  }, []);

  const filteredStudents = useMemo(() => {
    const f = normalize(studentFilter);
    if (!f) return students;
    return students.filter((s) =>
      `${s.fullName} ${s.studentKey} ${s.grade || ""}`.toLowerCase().includes(f)
    );
  }, [students, studentFilter]);

  const selectedStudentKeys = useMemo(
    () => students.filter((s) => selected[s.studentKey]).map((s) => s.studentKey),
    [students, selected]
  );

  const selectedStudentNames = useMemo(
    () =>
      students
        .filter((s) => selected[s.studentKey])
        .map((s) => s.fullName)
        .sort((a, b) => a.localeCompare(b)),
    [students, selected]
  );

  const displayTitle = useMemo(
    () =>
      autoAppendAudience
        ? buildSmartTitle(baseTitle, selectedStudentNames)
        : baseTitle.trim() || "Announcements",
    [autoAppendAudience, baseTitle, selectedStudentNames]
  );

  function toggleStudent(studentKey: string) {
    setSelected((prev) => ({ ...prev, [studentKey]: !prev[studentKey] }));
  }

  function selectAllFiltered() {
    const next = { ...selected };
    filteredStudents.forEach((s) => {
      next[s.studentKey] = true;
    });
    setSelected(next);
  }

  function clearAll() {
    setSelected({});
  }

  function saveAudience() {
    const name = saveName.trim();
    if (!name) {
      setErr("Enter a saved audience name.");
      return;
    }
    if (selectedStudentKeys.length === 0) {
      setErr("Select at least one student before saving an audience.");
      return;
    }

    const next: SavedAudience[] = [
      {
        id: makeId(),
        name,
        baseTitle: baseTitle.trim() || "Announcements",
        studentKeys: selectedStudentKeys,
        createdAtIso: new Date().toISOString(),
      },
      ...savedAudiences,
    ];

    setSavedAudiences(next);
    writeSavedAudiences(next);
    setSaveName("");
    setMsg(`Saved audience "${name}".`);
    setErr(null);
  }

  function loadAudience(a: SavedAudience) {
    const next: Record<string, boolean> = {};
    a.studentKeys.forEach((k) => {
      next[k] = true;
    });
    setSelected(next);
    setBaseTitle(a.baseTitle || "Announcements");
    setMsg(`Loaded audience "${a.name}".`);
    setErr(null);
  }

  function deleteAudience(id: string) {
    const next = savedAudiences.filter((a) => a.id !== id);
    setSavedAudiences(next);
    writeSavedAudiences(next);
  }

  async function createAnnouncement() {
    if (selectedStudentKeys.length === 0) {
      setErr("Select at least one student.");
      return;
    }

    setBusy(true);
    setErr(null);
    setMsg(null);

    try {
      const fn = httpsCallable(functions, "createBroadcastThread");
      const res: any = await fn({
        title: displayTitle,
        studentKeys: selectedStudentKeys,
        reuse: true,
      });

      const threadId = String(res?.data?.threadId || "");
      if (!threadId) throw new Error("createBroadcastThread did not return a threadId.");

      nav(`/messaging?threadId=${encodeURIComponent(threadId)}`);
    } catch (e: any) {
      setErr(e?.message || "Failed to create/open announcement thread.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <h2 style={{ marginTop: 0 }}>New Announcement</h2>
          <p className="muted" style={{ marginTop: 6 }}>
            Create or reopen a one-way broadcast thread to parents for the selected student audience.
          </p>
        </div>

        <div>
          <button className="btn secondary" onClick={() => nav("/messaging")}>
            Back to Messaging
          </button>
        </div>
      </div>

      {msg ? <div className="success">{msg}</div> : null}
      {err ? <div className="error">{err}</div> : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 360px",
          gap: 16,
          marginTop: 16,
        }}
      >
        <div className="card" style={{ padding: 12, background: "var(--surface)" }}>
          <h3 style={{ marginTop: 0 }}>Audience Builder</h3>

          <div className="grid2">
            <div>
              <div className="label">Base title</div>
              <input
                className="input"
                value={baseTitle}
                onChange={(e) => setBaseTitle(e.target.value)}
                placeholder="Announcements"
              />
            </div>

            <div>
              <div className="label">Auto-append audience</div>
              <label style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 10 }}>
                <input
                  type="checkbox"
                  checked={autoAppendAudience}
                  onChange={(e) => setAutoAppendAudience(e.target.checked)}
                />
                <span>Append student name / count</span>
              </label>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="label">Title preview</div>
            <div className="card" style={{ padding: 12, background: "var(--card)" }}>
              <b>{displayTitle}</b>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="label">Search students</div>
            <input
              className="input"
              value={studentFilter}
              onChange={(e) => setStudentFilter(e.target.value)}
              placeholder="name, key, grade…"
            />
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            <button className="btn secondary" onClick={selectAllFiltered}>
              Select Filtered
            </button>
            <button className="btn secondary" onClick={clearAll}>
              Clear
            </button>
          </div>

          <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
            {selectedStudentKeys.length} selected • {filteredStudents.length} shown
          </div>

          <hr className="hr" />

          <div style={{ maxHeight: 520, overflow: "auto", display: "grid", gap: 8 }}>
            {loadingStudents ? (
              <div className="muted">Loading students…</div>
            ) : filteredStudents.length === 0 ? (
              <div className="muted">No students found.</div>
            ) : (
              filteredStudents.map((s) => {
                const selectedNow = !!selected[s.studentKey];
                return (
                  <button
                    key={s.studentKey}
                    type="button"
                    onClick={() => toggleStudent(s.studentKey)}
                    style={{
                      textAlign: "left",
                      borderRadius: 12,
                      border: selectedNow ? "2px solid var(--primary)" : "1px solid var(--border)",
                      background: selectedNow ? "var(--primary-soft)" : "var(--card)",
                      padding: 12,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input type="checkbox" checked={selectedNow} readOnly />
                      <div>
                        <div style={{ fontWeight: 800 }}>{s.fullName}</div>
                        <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                          {s.studentKey}{s.grade ? ` • Grade ${s.grade}` : ""}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="card" style={{ padding: 12, background: "var(--surface)" }}>
          <h3 style={{ marginTop: 0 }}>Saved Audiences</h3>
          <p className="muted" style={{ marginTop: 6 }}>
            Saved in this browser for fast reuse.
          </p>

          <div className="label">Save current audience</div>
          <input
            className="input"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="e.g. Mrs. Smith Homeroom"
          />
          <div style={{ marginTop: 10 }}>
            <button className="btn secondary" onClick={saveAudience}>
              Save Audience
            </button>
          </div>

          <hr className="hr" />

          <div style={{ maxHeight: 320, overflow: "auto", display: "grid", gap: 8 }}>
            {savedAudiences.length === 0 ? (
              <div className="muted">No saved audiences yet.</div>
            ) : (
              savedAudiences.map((a) => (
                <div
                  key={a.id}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    padding: 12,
                    background: "var(--card)",
                  }}
                >
                  <div style={{ fontWeight: 800 }}>{a.name}</div>
                  <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                    {a.studentKeys.length} students • {a.baseTitle || "Announcements"}
                  </div>

                  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <button className="btn small secondary" onClick={() => loadAudience(a)}>
                      Load
                    </button>
                    <button className="btn small secondary" onClick={() => deleteAudience(a.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <hr className="hr" />

          <div className="label">Create announcement</div>
          <div className="card" style={{ padding: 12, background: "var(--card)" }}>
            <div><b>Title:</b> {displayTitle}</div>
            <div style={{ marginTop: 8 }}><b>Audience size:</b> {selectedStudentKeys.length}</div>
            <div style={{ marginTop: 8 }} className="muted">
              This opens a one-way broadcast thread. Parents cannot reply in that thread.
            </div>
          </div>

          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
            <button
              className="btn"
              disabled={busy || selectedStudentKeys.length === 0}
              onClick={createAnnouncement}
            >
              {busy ? "Creating…" : "Create / Open Announcement"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
